import type { RescheduleChangeDTO } from 'src/types/dto/reschedule-suggestion.dto';
import type { AppointmentDTO } from 'src/types/dto/appointment.dto';
import type { InspectorDTO } from 'src/types/dto/inspector.dto';

import type { ReactNode } from 'react';

import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Divider, Radio, Stack, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { AppointmentRangeLabel } from './appointment-range-label';
import { EntityLinkManager } from 'src/components/EntityLinkManager';
import { Iconify } from 'src/components/iconify';
import { Label } from 'src/components/label';
import { TagsList, type Tag } from 'src/components/tags-list';
import { getAuthorizationName } from 'src/utils/competency';

import {
  formatRescheduleDetailDate,
  getActivityCompetencyTags,
  getActivityCurrentAppointments,
  getEarliestNonBlockerAppointment,
  getInspectorBusinessUnit,
  getActivityCompetencyIds,
  getInspectorAuthorizationsForActivity,
  getActivityDisplayDurationDays,
  getTravelSummary,
} from './utils';

interface DetailFieldProps {
  label: string;
  children: ReactNode;
}

const DetailField = memo(({ label, children }: DetailFieldProps) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ width: 100, flexShrink: 0, pt: 0 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
));

DetailField.displayName = 'DetailField';

interface ComparisonColumnProps {
  title: string;
  appointments: AppointmentDTO[];
  siteTimezoneId?: string;
  daysLabel: string;
  inspectorName: string;
  competencyContainerId: string;
  authorizationTags: Tag[];
  businessUnit: string;
  travelLabel: string;
  selected?: boolean;
}

const ComparisonColumn = memo(
  ({
    title,
    appointments,
    siteTimezoneId,
    daysLabel,
    inspectorName,
    competencyContainerId,
    authorizationTags,
    businessUnit,
    travelLabel,
    selected = false,
  }: ComparisonColumnProps) => (
    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle1">{title}</Typography>
      <AppointmentRangeLabel appointments={appointments} siteTimezoneId={siteTimezoneId} />
      <Typography variant="caption">{daysLabel}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Radio checked={selected} size="small" sx={{ p: 0 }} disabled />
        <Typography variant="caption" sx={{ textDecoration: 'underline' }}>
          {inspectorName}
        </Typography>
      </Box>
      <Box id={competencyContainerId} sx={{ width: '100%', minHeight: 24, maxHeight: 24 }}>
        <TagsList
          containerIdentifier={competencyContainerId}
          tags={authorizationTags}
          color="info"
          variant="filled"
          showTooltip
        />
      </Box>
      <Typography variant="caption">{businessUnit}</Typography>
      <Typography variant="caption">{travelLabel}</Typography>
    </Stack>
  )
);

ComparisonColumn.displayName = 'ComparisonColumn';

interface InspectorRescheduleCardChangeDetailsProps {
  change: RescheduleChangeDTO;
  proposedInspector: InspectorDTO;
  isDeskService?: boolean;
  siteTimezoneId?: string;
  onDismiss?: () => void;
}

export const InspectorRescheduleCardChangeDetails = memo(
  ({ change, proposedInspector, isDeskService = false, siteTimezoneId, onDismiss }: InspectorRescheduleCardChangeDetailsProps) => {
    const { t, i18n } = useTranslation(['shuffle', 'common']);

    const activity = change.activity;
    const currentAppointments = useMemo(
      () => getActivityCurrentAppointments(activity),
      [activity]
    );
    const proposedAppointments = change.newAppointment;
    const currentPrimaryAppointment = useMemo(
      () => getEarliestNonBlockerAppointment(currentAppointments),
      [currentAppointments]
    );
    const currentInspector = currentPrimaryAppointment?.inspector;
    const currentTravel = getTravelSummary(currentAppointments, isDeskService);
    const proposedTravel = getTravelSummary(proposedAppointments, isDeskService);

    const activityCompetencyIds = useMemo(
      () => getActivityCompetencyIds(activity),
      [activity]
    );

    const activityCompetencyTags = useMemo(
      () => getActivityCompetencyTags(activity),
      [activity]
    );

    const mapInspectorAuthorizationTags = useMemo(
      () => (inspector?: InspectorDTO) =>
        getInspectorAuthorizationsForActivity(inspector, activityCompetencyIds).map(
          (authorization) => ({
            id: authorization.id,
            label: getAuthorizationName(authorization, i18n),
            color: 'info' as const,
            variant: 'filled' as const,
          })
        ),
      [activityCompetencyIds, i18n]
    );

    const currentAuthorizationTags = useMemo(
      () => mapInspectorAuthorizationTags(currentInspector),
      [currentInspector, mapInspectorAuthorizationTags]
    );

    const proposedAuthorizationTags = useMemo(
      () => mapInspectorAuthorizationTags(proposedInspector),
      [mapInspectorAuthorizationTags, proposedInspector]
    );

    const activityWindow = useMemo(() => {
      if (!activity.startDate && !activity.dueDate) {
        return '-';
      }

      const start = activity.startDate ? formatRescheduleDetailDate(activity.startDate, siteTimezoneId) : '';
      const end = activity.dueDate ? formatRescheduleDetailDate(activity.dueDate, siteTimezoneId) : '';

      if (start && end) {
        return `${start} - ${end}`;
      }

      return start || end || '-';
    }, [activity.dueDate, activity.startDate, siteTimezoneId]);

    const activityCompetencyContainerId = `inspector-reschedule-activity-competencies-${activity.id}`;
    const clientId = activity.standard?.project?.customer?.id ?? activity.customer?.id;
    const clientName = activity.standard?.project?.customer?.name ?? activity.customer?.name ?? '-';
    const projectId = activity.standard?.project?.id;
    const projectLabel =
      activity.standard?.project?.orderReference ?? activity.standard?.project?.name ?? '-';
    const projectHref = projectId
      ? paths.dashboard.projects.details(String(projectId))
      : undefined;

    return (
      <Box
        sx={{
          display: 'flex',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Stack spacing={0.5} sx={{ flex: 1, py: 2, pl: 2, pr: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1">
              {t('shuffle:inspectorRescheduleCard.activityTitle', { id: activity.id })}
            </Typography>
            <Label variant="soft" color="secondary">
              {t(`common:activityStatus.${activity.status}`)}
            </Label>
          </Box>

          {clientName !== '-' ? (
            clientId ? (
              <EntityLinkManager
                entityId={clientId}
                entityType="Client"
                label={clientName}
                variant="caption"
                onClose={onDismiss}
              />
            ) : (
              <Typography variant="caption">{clientName}</Typography>
            )
          ) : null}

          <DetailField label={t('shuffle:inspectorRescheduleCard.fields.window')}>
            <Typography variant="caption">{activityWindow}</Typography>
          </DetailField>
          <DetailField label={t('shuffle:inspectorRescheduleCard.fields.days')}>
            <Typography variant="caption">
              {getActivityDisplayDurationDays(activity, proposedAppointments) || '-'}
            </Typography>
          </DetailField>
          <DetailField label={t('shuffle:inspectorRescheduleCard.fields.competencies')}>
            <Box
              id={activityCompetencyContainerId}
              sx={{ width: '100%', minHeight: 24, maxHeight: 24 }}
            >
              <TagsList
                containerIdentifier={activityCompetencyContainerId}
                tags={activityCompetencyTags}
                color="info"
                variant="filled"
                showTooltip
              />
            </Box>
          </DetailField>
          <DetailField label={t('shuffle:inspectorRescheduleCard.fields.project')}>
            {projectId ? (
              <EntityLinkManager
                entityId={projectId}
                entityType="Project"
                label={projectLabel}
                href={projectHref}
                variant="caption"
                onClose={onDismiss}
              />
            ) : (
              <Typography variant="caption">{projectLabel}</Typography>
            )}
          </DetailField>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ display: 'flex', flex: 1, minWidth: 0, py: 2, pr: 4, pl: 2 }}>
          <ComparisonColumn
            title={t('shuffle:inspectorRescheduleCard.current')}
            appointments={currentAppointments}
            siteTimezoneId={siteTimezoneId}
            daysLabel={String(getActivityDisplayDurationDays(activity, currentAppointments) || '-')}
            inspectorName={currentInspector?.name ?? '-'}
            competencyContainerId={`inspector-reschedule-current-competencies-${activity.id}-${currentInspector?.id ?? 'none'}`}
            authorizationTags={currentAuthorizationTags}
            businessUnit={getInspectorBusinessUnit(currentInspector)}
            travelLabel={currentTravel.durationLabel}
            selected
          />

          <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
            <Iconify icon="eva:arrow-forward-fill" width={24} sx={{ color: 'text.disabled' }} />
          </Box>

          <ComparisonColumn
            title={t('shuffle:inspectorRescheduleCard.proposed')}
            appointments={proposedAppointments}
            siteTimezoneId={siteTimezoneId}
            daysLabel={String(getActivityDisplayDurationDays(activity, proposedAppointments) || '-')}
            inspectorName={proposedInspector.name}
            competencyContainerId={`inspector-reschedule-proposed-competencies-${activity.id}-${proposedInspector.id}`}
            authorizationTags={proposedAuthorizationTags}
            businessUnit={getInspectorBusinessUnit(proposedInspector)}
            travelLabel={proposedTravel.durationLabel}
          />
        </Box>
      </Box>
    );
  }
);

InspectorRescheduleCardChangeDetails.displayName = 'InspectorRescheduleCardChangeDetails';
